<template>
  <div>
    <v-toolbar role="navigation"
               class="student--toolbar">
      <v-layout row class="toolbar--content container">
        <img :src="`${getLogoUrl}`"
             class="uni-logo"
             :alt="$t('university_logo')" />
        <ul class="student--navs">
          <li class="student--list">
            <v-btn v-for="nav in navs"
                   :key="nav.id"
                   :id="`menu_${nav.id}`"
                   :to="nav.destination"
                   :aria-label="nav.label ? getLabel(nav.label) : getLabel(nav.text)"
                   class="student--link">
              {{ getLabel(nav.text) }}
            </v-btn>
            <v-btn v-if="this.announcements.length > 0"
                   id="menu_announce"
                   class="focusable student--link student--announcements"
                   to="announcements"
            >{{ getLabel('student_view_announcements') }}</v-btn>
            <v-btn
              id="menu_logout"
              @click="logout"
              class="focusable student--link student--logout"
              role="button"
              :aria-label="$t('logout')"
            >{{ getLabel('student_view_logout') }}</v-btn>
          </li>
        </ul>
      </v-layout>
    </v-toolbar>
    <accessibility-control/>
  </div>
</template>

<script>
import AccessibilitySwitch from '../../../components/navbar/AccessibilitySwitch'
import {mapActions, mapGetters, mapState} from 'vuex'
import {GET_LOGO_URL, HAS_FEATURE_FLAG, IS_ACCESSIBLE} from '../../../store/getter-types'
import {LOGOUT} from '../../../store/action-types'
import {setMainTheme} from '../../../utils/theme'
import {setSubTheme} from '@/utils/theme'
import AccessibilityControl from '../../../components/student/accessible/AccessibilityControl'

const studentViews = [
  {
    id: 'student_view_profile',
    destination: 'profile',
    text: 'student_view_profile'
  },
  {
    id: 'student_view_engagement',
    destination: 'engagement',
    text: 'student_view_engagement'
  },
  {
    id: 'student_view_resources',
    destination: 'resources',
    text: 'student_view_resources'
  },
  {
    id: 'student_view_interactions',
    destination: 'interactions',
    text: 'student_view_interactions'
  },
  {
    id: 'student_view_modules',
    destination: 'modules',
    text: 'student_view_modules'
  },
  {
    id: 'student_view_attendance',
    destination: 'attendance',
    text: 'student_view_attendance'
  },
  {
    id: 'student_view_assessments',
    destination: 'assessments',
    text: 'student_view_assessments'
  },
  {
    id: 'student_view_faqs',
    destination: 'faqs',
    text: 'student_view_faqs',
    label: 'student_view_faqs_label'
  }
  // {
  //   id: 'student_view_settings',
  //   destination: 'settings',
  //   text: 'Settings'
  // }
]

export default {
  name: 'StudentView',
  components: {AccessibilitySwitch, AccessibilityControl},
  data: () => {
    return {}
  },
  computed: {
    ...mapGetters({
      getLogoUrl: GET_LOGO_URL,
      isAccessible: IS_ACCESSIBLE,
      hasFeatureFlag: HAS_FEATURE_FLAG
    }),
    ...mapState({
      pageTitle: state => state.config.pageTitle,
      announcements: state => state.announcement.announcements,
      moduleInterExem: state => state.profile.module.interexem,
      functionStudentInteractions: state => state.student.functions.studentInteractions,
      optionEnableInteractionsTab: state => state.student.options.enable.interactionsTab,
      moduleMods: state => state.profile.module.studentmods,
      functionStudentModules: state => state.student.functions.studentModules,
      optionsEnableModulesTab: state => state.student.options.enable.modulesTab
    }),
    navs () {
      let featureFlagChecker = []
      if (!this.flagEnableInteractionsTab) {
        featureFlagChecker.push('student_view_interactions')
      }
      if (!this.flagEnableModulesTab) {
        featureFlagChecker.push('student_view_modules')
      }
      return studentViews.filter(nav => !featureFlagChecker.includes(nav.id))
    },
    flagEnableInteractionsTab () {
      return this.hasFeatureFlag(this.moduleInterExem + '.' + this.functionStudentInteractions + '.' + this.optionEnableInteractionsTab)
    },
    flagEnableModulesTab () {
      return this.hasFeatureFlag(this.moduleMods + '.' + this.functionStudentModules + '.' + this.optionsEnableModulesTab)
    }
  },
  created () {
    if (!this.isAccessible) {
      this.$router.push('/student')
    }
  },
  mounted () {
    this.setMainAndSubTheme()
  },
  methods: {
    ...mapActions({
      doLogout: LOGOUT
    }),
    getLabel (key) {
      if (this.$i18n.keyExists(key)) {
        return this.$t(key)
      } else {
        return key
      }
    },
    setMainAndSubTheme () {
      this.$vuetify.theme = setMainTheme('light')
      this.$vuetify.theme = setSubTheme('default')
    },
    logout () {
      this.doLogout()
      if (!this.$store.state.config.useSSO && !this.$store.state.config.useSAML) {
        this.$router.push({name: 'Login'})
      } else {
        window.location.replace(this.$store.state.config.authUrlLogOut)
      }
    }
  },
  beforeRouteLeave (to, from) {
    if (this.pageTitle !== undefined && this.pageTitle !== '') {
      document.title = this.pageTitle
    }
  }
}
</script>

<style scoped>
.uni-logo {
  max-height: 65px;
  max-width: 150px;
  margin: 10px;
}

.student--toolbar {
  background-color: #003b4d;
}

.student--toolbar >>> .toolbar__content {
  height: auto !important;
  min-height: 56px;
}

.toolbar--content {
  align-items: center;
  flex-wrap: wrap;
}

.student--navs {
  display: flex;
  flex-wrap: wrap;
}

.student--list {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
}

.student--link {
  font-size: inherit;
  height: auto;
}

.student--link >>> .btn__content {
  max-width: 275px;
  width: -moz-available;          /* WebKit-based browsers will ignore this. */
  width: -webkit-fill-available;  /* Mozilla-based browsers will ignore this. */
  width: fill-available;
  min-width: 100px;
  min-height: 36px;
  overflow-wrap: break-word;
  word-break: break-word;
  white-space: normal;
}

.student--link:focus {
  outline-offset: -5px;
  outline: black dashed 3px;
}

@media (max-width: 1264px) {
  .toolbar--content {
    justify-content: space-between;
  }
}

@media (max-width: 800px) {
  .toolbar--content {
    flex-direction: column !important;
  }

  .student--list {
    grid-template-columns: 1fr 1fr;
  }
}

.secondary--nav {
  list-style: none;
}

.student--announcements {
  grid-column-end: -2;
}

.student--logout {
  grid-column-end: -1;
}
</style>



// WEBPACK FOOTER //
// src/views/student/accessible/StudentView.vue